import React, { useState } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Heart } from 'lucide-react';
import { HashLink } from 'react-router-hash-link';
import ContactForm from './ContactForm';
import { useLanguage } from '../utils/LanguageContext';
import content from "../content/footer.json"


const Footer = () => {

  const { language, setLanguage } = useLanguage();
  const [showContactForm, setShowContactForm] = useState(false);

  const t = content[language];


  return (
    <>
      {
        showContactForm && (
          <ContactForm
            onClose={() => setShowContactForm(false)}
            language={language}
          />
        )
      }

      <footer className="bg-black text-white">
        <div className="py-14 max-w-7xl mx-auto px-4 sm:px-4 md:px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <h3 className="text-2xl font-bold text-gradient mb-4">
                <a href="/" target="_blank" className="text-2xl font-bold text-gradient">
                  {t.brand.name}
                </a>
              </h3>
              <p className="text-white mb-4">{t.brand.description}</p>
              <div className="flex space-x-4">
                <a
                  href={t.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-[#1AA06A] transition-colors"
                >
                  {/* LinkedIn SVG */}
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a
                  href={t.social.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-[#1AA06A] transition-colors"
                >
                  {/* Twitter SVG */}
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-semibold mb-4">{t.servicesTitle}</h4>
              <ul className="space-y-2 text-white">
                <li>
                  <HashLink smooth to="/#services" className="hover:text-[#1AA06A] transition-colors">
                    {t.services.oneOnOne}
                  </HashLink>
                </li>
                <li>
                  <HashLink smooth to="/#services" className="hover:text-[#1AA06A] transition-colors">
                    {t.services.corporate}
                  </HashLink>
                </li>
                <li>
                  <Link to="/method" className="hover:text-[#1AA06A] transition-colors">
                    {t.services.method}
                  </Link>
                </li>
                <li>
                  <Link to="/instructors" className="hover:text-[#1AA06A] transition-colors">
                    {t.services.instructors}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-lg font-semibold mb-4">{t.companyTitle}</h4>
              <ul className="space-y-2 text-white">
                <li>
                  <Link to="/about" className="hover:text-[#1AA06A] transition-colors">
                    {t.company.about}
                  </Link>
                </li>
                <li>
                  <Link to="/success-stories" className="hover:text-[#1AA06A] transition-colors">
                    {t.company.success}
                  </Link>
                </li>
                <li>
                  <Link onClick={() => setShowContactForm(true)} className="hover:text-[#1AA06A] transition-colors">
                    {t.company.booking}
                  </Link>
                </li>
                <li>
                  <a href="mailto:careers@lexora.com" className="hover:text-[#1AA06A] transition-colors">
                    {t.company.careers}
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-semibold mb-4">{t.contactTitle}</h4>
              <div className="space-y-3 text-white inline-flex flex-col">
                <a
                  href={`mailto:${t.contact.email}`}
                  className="inline-block items-center space-x-2 sm:space-x-2 md:space-x-2 xl:space-x-3 2xl:space-x-3 group"
                >
                  <Mail className="inline w-4 h-4 text-white group-hover:text-[#1AA06A]" />
                  <span className="group-hover:text-[#1AA06A] transition-colors">{t.contact.email}</span>
                </a>
                <a
                  href={`tel:${t.contact.phone}`}
                  className="inline-block items-center space-x-2 sm:space-x-2 md:space-x-2 xl:space-x-3 2xl:space-x-3 group"
                >
                  <Phone className="inline w-4 h-4 text-white group-hover:text-[#1AA06A} " />
                  <span className="group-hover:text-[#1AA06A] transition-colors">{t.contact.phone}</span>
                </a>
                <div className="inline-block items-center space-x-2 sm:space-x-2 md:space-x-2 xl:space-x-3 2xl:space-x-3">
                  <MapPin className="inline w-4 h-4 text-white" />
                  <span>{t.contact.location}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-white mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 md:space-x-5">
              {/* Legal Links */}
              <div className="flex flex-wrap gap-2 2xl:gap-4 xl:gap-4 md:gap-3 lg:gap-3 text-sm text-white">
                <Link to="/privacy-policy" className="hover:text-[#1AA06A] transition-colors">
                  {t.legalLinks.privacy}
                </Link>
                <Link to="/terms-of-service" className="hover:text-[#1AA06A] transition-colors">
                  {t.legalLinks.terms}
                </Link>
                <button
                  onClick={() => {
                    localStorage.removeItem("lexora-cookie-consent");
                    window.location.reload();
                  }}
                  className="hover:text-[#1AA06A] transition-colors"
                >
                  {t.legalLinks.cookies}
                </button>
                <a href="mailto:legal@lexora.com" className="hover:text-[#1AA06A] transition-colors">
                  {t.legalLinks.legal}
                </a>
              </div>

              {/* Copyright */}
              <div className="flex flex-wrap gap-2 2xl:gap-3 xl:gap-3 md:gap-3 lg:gap-2 text-sm text-white ">
                <span>{t.copyright}</span>
                <span className="text-gray-600">•</span>
                <span className="flex items-center space-x-1">
                  <span>{t.madeIn}</span>
                  <Heart className="w-3 h-3 text-red-400" />
                  <span>{t.locationMade}</span>
                </span>
              </div>
            </div>

            {/* Additional Legal Info */}
            <div className="mt-6 text-xs text-gray-500 text-center">
              <p>{t.additionalInfo}</p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};


export default Footer;